<script setup lang="ts">
import type {
  NovaImageContainerEmits,
  NovaImageContainerProps,
} from './NovaImageContainer.types'
import { LAZY_LOAD_IMAGE_DELAY_MS, LAZY_LOAD_IMAGE_MAX_RETRIES } from '@configs'

const emits = defineEmits<NovaImageContainerEmits>()
const props = withDefaults(defineProps<NovaImageContainerProps>(), {
  imageUrl: '',
  ratio: '4:3',
  responsiveSize: () => ({}),
  emptyType: 'default',
  isFillContainer: false,
  stroke: false,
  portraitXs: false,
  alt: '',
})
const backupImageSrc = ref(props.imageUrl)
const imageSrc = ref(props.imageUrl)
const retryCount = ref(0)
const isLoadSuccess = ref(false)
const isLoadFail = ref(false)
const loadStatus = computed(() => {
  if (!props.imageUrl) return 'LOAD_FAIL'
  if (!isLoadFail.value && !isLoadSuccess.value) return 'LOADING'
  if (isLoadFail.value) return 'LOAD_FAIL'
  if (isLoadSuccess.value) return 'LOAD_SUCCESS'
})
const ratioClass = computed(
  () => `ratio-${String(props.ratio).replace(':', 'x')}`
)
const emptyImageClass = computed(() => `empty-${props.emptyType.toLowerCase()}`)
const responsiveSizeClass = computed(() => {
  const keys = Object.keys(props.responsiveSize) as Responsive[]
  return keys.map(
    (key) => `${key}-ratio-${props.responsiveSize[key]?.replace(':', 'x')}`
  )
})

const handleOnLoad = () => {
  isLoadSuccess.value = true
  emits('onImgLoad')
}

const handleOnError = (evt: Event) => {
  if (!backupImageSrc.value) {
    isLoadFail.value = true
    return
  }

  retryCount.value++

  // Loading image failed. Retrying...
  if (retryCount.value <= LAZY_LOAD_IMAGE_MAX_RETRIES) {
    setTimeout(() => {
      const url = new URL(backupImageSrc.value)
      url.searchParams.append('v', String(retryCount.value))
      imageSrc.value = url.href
    }, LAZY_LOAD_IMAGE_DELAY_MS * retryCount.value)
  }

  // Loading image failed after maximum retries
  else {
    isLoadFail.value = true
    emits('onImgError')
    ;(evt.target as HTMLImageElement).style.display = 'none'
  }
}
</script>

<template>
  <div
    :class="[
      'image-container',
      ratioClass,
      emptyImageClass,
      ...responsiveSizeClass,
      { 'is-load-success': loadStatus === 'LOAD_SUCCESS' },
      { 'is-load-fail': loadStatus === 'LOAD_FAIL' },
      { 'portrait-xs': portraitXs },
      { 'fill-container': isFillContainer },
    ]"
  >
    <ClientOnly>
      <img
        :src="imageSrc"
        :alt="alt"
        loading="lazy"
        @load="handleOnLoad"
        @error="handleOnError"
      />
    </ClientOnly>

    <ClientOnly>
      <NovaLoadingIndicator
        v-if="props.imageUrl && loadStatus === 'LOADING'"
        :bg-bright="'light'"
        :overlay="true"
      />
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
$responsive-items: ('', 'desktop-', 'tablet-', 'mobile-');

@mixin setRatio($prefix) {
  &.#{$prefix}ratio-16x9::before {
    padding-top: 56.25%;
  }
  &.#{$prefix}ratio-15x8::before {
    padding-top: 53.33333333%;
  }
  &.#{$prefix}ratio-1x1::before {
    padding-top: 100%;
  }
  &.#{$prefix}ratio-2x1::before {
    padding-top: 50%;
  }
  &.#{$prefix}ratio-3x2::before {
    padding-top: 66.6666666667%;
  }
  &.#{$prefix}ratio-4x1::before {
    padding-top: 25%;
  }
  &.#{$prefix}ratio-4x3::before {
    padding-top: 75%;
  }
  &.#{$prefix}ratio-4x5::before {
    padding-top: 125%;
  }
  &.#{$prefix}ratio-3x4::before {
    padding-top: 133.3333333333%;
  }
  &.#{$prefix}ratio-3x5::before {
    padding-top: 166.6666666667%;
  }
  &.#{$prefix}ratio-5x1::before {
    padding-top: 20%;
  }
  &.#{$prefix}ratio-5x2::before {
    padding-top: 40%;
  }
  &.#{$prefix}ratio-6x5::before {
    padding-top: 84.212%;
  }
  &.#{$prefix}ratio-auto {
    width: 100%;
    height: auto;

    &:before {
      display: none;
    }

    &.is-load-fail {
      min-height: 100px;
    }

    > img {
      position: relative;
      height: auto;
      object-fit: initial;
    }
  }
  &.#{$prefix}ratio-640x185::before {
    padding-top: 28.90625%;
  }
  &.#{$prefix}ratio-380x213::before {
    padding-top: 56.05263157894737%;
  }
  &.#{$prefix}ratio-20x7::before {
    padding-top: 35%;
  }
  &.#{$prefix}ratio-20x3::before {
    padding-top: 15%;
  }
  &.#{$prefix}ratio-100x85::before {
    padding-top: 85%;
  }
  &.#{$prefix}ratio-10x1::before {
    padding-top: 10%;
  }
  &.#{$prefix}ratio-10x3::before {
    padding-top: 30%;
  }
  &.#{$prefix}ratio-335x84::before {
    padding-top: 25.07462686567%;
  }
}

@mixin ratios() {
  @each $responsive in $responsive-items {
    @if $responsive == '' {
      @include setRatio('');
    } @else if $responsive == 'desktop-' {
      @include setRatio('');
    } @else if $responsive == 'tablet-' {
      @include tablet {
        @include setRatio('tablet-');
      }
    } @else if $responsive == 'mobile-' {
      @include mobile {
        @include setRatio('mobile-');
      }
    }
  }
}

.image-container {
  position: relative;
  flex-shrink: 0;
  width: 100%;
  padding: 0;
  overflow: hidden;
  background-color: inherit;

  &::before {
    display: block;
    content: '';
    padding-top: 56.25%;
  }

  @include ratios;

  &.portrait-xs.empty-portrait:after {
    background-size: 12px auto;
  }

  &.fill-container {
    height: 100%;
    &::before {
      padding-top: 100%;
    }
  }

  &.is-load-success {
    > img {
      opacity: 1;
    }
  }

  &.is-load-fail {
    &.empty {
      @mixin base-empty-container {
        position: absolute;
        top: 50%;
        left: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        transform: translate(-50%, -50%);
      }

      &-default {
        &:after {
          @include base-empty-container;
          content: '';
          width: 24px;
          height: 24px;
          background-repeat: no-repeat;
          background-image: url('assets/images/empty-image-default.svg');
        }
      }

      &-portrait {
        &:after {
          @include base-empty-container;
          content: '';
          background-size: 24px auto;
          background-repeat: no-repeat;
          background-position: center;
          background-color: $color-bg-1;
          background-image: url('assets/images/empty-image-portrait.svg');
        }
      }

      &-magnifier {
        &::after {
          @include base-empty-container;
          width: 100%;
          height: 100%;
          content: '\e97d';
          font-family: 'lmnova';
          font-size: 24px;
        }
      }

      &-user-circle {
        &::after {
          @include base-empty-container;
          width: 100%;
          height: 100%;
          content: '\e9a0';
          font-family: 'lmnova';
          font-size: 24px;
        }
      }
    }
  }

  > img {
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
  }

  //.empty-image {
  //  position: absolute;
  //  top: 50%;
  //  left: 50%;
  //  transform: translate(-50%, -50%);
  //
  //  &.portrait {
  //    width: 100%;
  //    height: 100%;
  //    background-color: $color-bg-1;
  //    background-size: 24px auto;
  //    background-repeat: no-repeat;
  //    background-position: center;
  //    background-image: url('assets/images/empty-image-portrait.svg');
  //  }
  //
  //  &.default {
  //    width: 24px;
  //    height: 24px;
  //    background-size: 100%;
  //    background-image: url('assets/images/empty-image-default.svg');
  //  }
  //
  //  &.magnifier {
  //    display: flex;
  //    align-items: center;
  //    justify-content: center;
  //    width: 100%;
  //    height: 100%;
  //    &::after {
  //      content: '\e97d';
  //      font-family: 'lmnova';
  //      font-size: 24px;
  //    }
  //  }
  //}

  //.loading-indicator {
  //  position: absolute;
  //  top: 0;
  //  left: 0;
  //  background-color: $color-bg-1;
  //
  //  &.done {
  //    opacity: 0;
  //  }
  //}
}
</style>
